<template>
  <div class="bg-light rounded mt-10 py-5 px-5 lg:px-10">
    <div class="mb-4">
      <div class="flex flex-col gap-4 md:flex-row md:justify-between items-start md:items-center">
        <h3 class="flex text-xlx justify-start items-center gap-2">
          <span
              class="h-12 w-12 bg-dark text-light rounded-full inline-flex justify-center items-center leading-none">
            <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z"
                  fill="currentColor"/>
            </svg>
          </span>
          <span>Secure Payment</span>
        </h3>
        <img :src="`${$settings.theme_url}/assets/images/cards.png`" class="h-4 w-auto inline-block"
             alt="card logos, visa, mastercard, amex">
      </div>
    </div>
    <div class="flex flex-col gap-4">
      <div v-if="!stripeLoaded || store.totalDueCents <= 0" class="py-8 text-center text-gray-500">
        Loading payment form...
      </div>
      <VueStripeProvider
          :publishable-key="stripeKey"
          @load="onStripeLoad"
          @error="onStripeLoadError"
      >
        <VueStripeElements
            v-if="stripeLoaded && store.totalDueCents > 0"
            :options="elementsOptions"
            @ready="onElementsReady"
            @error="onStripeElementsError"
            ref="elementsComponent"
            class="pb-4"
        >
          <VueStripePaymentElement
              @change="emit('errors', [])"
              :options="paymentElementOptions"
              ref="paymentComponent"
          />
        </VueStripeElements>
      </VueStripeProvider>

      <FieldError v-if="error !== ''" :errors="[error]"/>
    </div>
  </div>
</template>
<script setup>
import {defineProps, ref, useTemplateRef, watch} from "vue"
import {
  VueStripeProvider,
  VueStripeElements,
  VueStripePaymentElement
} from '@vue-stripe/vue-stripe'
import {storeToRefs} from "pinia";
import useStore from './../store'
import FieldError from "./FieldError.vue";
import {trackBookingComplete} from "../helpers";
import { useStripeLoadWarning } from "../helpers/stripe-loading";

const store = useStore();
const error = ref('')
const ready = ref(true)
const token = ref('')
const stripeInstance = ref(null)
const elementsInstance = ref(null)

const props = defineProps({
  price: {
    type: [String, Number],
    default: 0,
  },
  config: {
    type: Object,
    default: () => ({})
  }
});

/**
 * DEFERRED PAYMENT
 * This Payment Element example is based on
 * https://docs.stripe.com/payments/accept-a-payment-deferred?type=payment
 */

// const stripeKey = "pk_test_OKDoqGqz6yprOyyhtChnHXVq"
const stripeKey = props.config.token;

const elementsOptions = ref({
  // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
  mode: "payment",
  amount: store.totalDueCents,
  currency: "aud",
  loader: 'always',
})

const paymentElementOptions = ref({
  paymentMethodOrder: [
    'card',
    'apple_pay',
    'google_pay',
    'link'
  ],
  layout: {
    type: 'tabs',
  }
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options
})
const stripeLoaded = ref(false)
const tmpBooking = ref({})

const elementsComponent = useTemplateRef("elementsComponent")
const paymentComponent = useTemplateRef("paymentComponent")

const emit = defineEmits([
    'errors',
    'setPage',
    'top'
]);

const onStripeLoad = (stripe) => {
  clearStripeLoadWarning()
  error.value = ''
  stripeInstance.value = stripe
  stripeLoaded.value = true
  emit('errors', [])
}

const onElementsReady = (elements) => {
  elementsInstance.value = elements
  if (store.totalDueCents > 0) {
    elements.update({amount: store.totalDueCents})
  }
}

const {
  clearStripeLoadWarning,
  showStripeLoadWarning,
} = useStripeLoadWarning({
  stripeLoaded,
  emit,
  setError: (message) => {
    error.value = message
  },
})

const onStripeLoadError = (details) => {
  clearStripeLoadWarning()
  showStripeLoadWarning(details)
}

const onStripeElementsError = (details) => {
  showStripeLoadWarning(details)
}

watch(
    () => store.totalDueCents,
    (amount) => {
      if (amount > 0){
        prepareElements(amount)
      }
    },
    { immediate: true }
)

function prepareElements(amount = store.totalDueCents) {
  elementsOptions.value = {
    ...elementsOptions.value,
    amount,
  }
  if (elementsInstance.value?.update) {
    elementsInstance.value.update({amount})
  }
}

const prepareIntent = async () => {
  return await store.preparePayment();
};


const handleError = (details) => {
  console.error(details)
  error.value = details.message;
}

const generate = async () => {
  //const intent = await prepareIntent()

  // Confirm the PaymentIntent using the details collected by the Payment Element
  if (!stripeInstance.value || !elementsInstance.value) {
    emit('errors', ['Please wait for the payment form to finish loading.']);
    return false;
  }

  elementsInstance.value.update({amount: store.totalDueCents });
  const result = await elementsInstance.value.submit();

  console.log('RESULT', result)
  //console.log('intent', intent)
  //console.log('intent.client_secret', intent.client_secret)

  if (result.error) {
    emit('errors', [result.error.message])
    return;
  }

  if (!tmpBooking.value.id){
    //error.value = 'System error when creating order';
    //return;
  }

  //if (['google_pay', 'apple_pay'].includes(result.selectedPaymentMethod)) {}
  // Create the ConfirmationToken using the details collected by the Payment Element
  // and additional shipping information
  // console.log('store.customer', store.customer)
  // console.log('stripeInstance', stripeInstance)

  if (stripeInstance.value) {
    const { error, confirmationToken } = await stripeInstance.value.createConfirmationToken({
      elements: elementsInstance.value,
      params: {
        payment_method_data: {
          billing_details: {
            name: `${store.customer.firstName} ${store.customer.lastName}`,
            email: store.customer.email,
            phone: store.customer.phone,
          }
        },
        return_url: store.options.confirm_url,
      }
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // creating the ConfirmationToken. Show the error to your customer (for example, payment details incomplete)
      //handleError(error);
      console.error(error)

      emit('errors', [error.message])
      return;
    }

    store.setPayment({
      confirmation: confirmationToken
    })

    //return true;
    console.log('confirmationToken', confirmationToken)

    return true;
  }

}

const handleServerResponse = async (data) => {
  console.log('handleServerResponse', data)

  if (data.error) {
    // Show error from server on payment form
    console.error(data.error)
    emit('errors', [data.error.message])

  } else if (data?.intent?.status === "requires_action") {
    // Use Stripe.js to handle the required next action
    const {
      error,
      paymentIntent
    } = await stripeInstance.value.handleNextAction({
      clientSecret: data?.intent?.client_secret
    });

    if (error) {
      // Show error from Stripe.js in payment form
      console.error(error)

      emit('errors', [error.message])

    } else {
      // Actions handled, show success message
      console.log('paymentIntent', paymentIntent)
      store.setBooking(data.booking);
      store.setBookingToken(data?.retailBooking?.token ?? data?.booking?.token ?? data?.token ?? false);
      store.setStatus("success");
      trackBookingComplete(data.booking)
      emit('setPage', 'thanks');
      emit('top')
    }
  } else {
    // No actions needed, show success message

    store.setBooking(data.booking);
    store.setBookingToken(data?.retailBooking?.token ?? data?.booking?.token ?? data?.token ?? false);
    store.setStatus("success");
    trackBookingComplete(data.booking)
    emit('setPage', 'thanks');
    emit('top')
  }
}

defineExpose({
  generate,
  handleServerResponse
})

</script>
